import { apiClient } from '../utils/apiClient';
import type {
    PublicAssessment,
    CreateAssessmentRequest,
    AssessmentListResponse,
    SessionGradingStatus
} from '@arrowise/shared';

class AssessmentService {
    // Create or update an assessment for a session
    async createOrUpdateAssessment(sessionId: string, payload: CreateAssessmentRequest): Promise<PublicAssessment> {
        const response = await apiClient.put<{ assessment: PublicAssessment }>(`/assessments/${sessionId}/assessments`, payload);
        if (!response.data?.assessment) throw new Error('Failed to create/update assessment');
        return response.data.assessment;
    }

    // Get all assessments for a session
    async getAssessmentsForSession(sessionId: string): Promise<PublicAssessment[]> {
        const response = await apiClient.get<AssessmentListResponse>(`/assessments/${sessionId}/assessments`);
        if (!response.data?.assessments) throw new Error('Failed to fetch assessments');
        return response.data.assessments;
    }

    // Get a specific assessment by assessor
    async getAssessment(sessionId: string, assessorId: string): Promise<PublicAssessment> {
        const response = await apiClient.get<{ assessment: PublicAssessment }>(`/assessments/${sessionId}/assessments/${assessorId}`);
        if (!response.data?.assessment) throw new Error('Failed to fetch assessment');
        return response.data.assessment;
    }

    // Delete an assessment (current user or any if user has permission)
    async deleteAssessment(sessionId: string, assessorId?: string): Promise<void> {
        const url = assessorId
            ? `/assessments/${sessionId}/assessments?assessorId=${assessorId}`
            : `/assessments/${sessionId}/assessments`;
        const response = await apiClient.delete<{ success: boolean; message?: string }>(url);
        if (!response.success) throw new Error(response.message || 'Failed to delete assessment');
    }

    // Get session grading status
    async getSessionGradingStatus(sessionId: string): Promise<SessionGradingStatus> {
        const response = await apiClient.get(`/assessments/${sessionId}/grading-status`) as any;
        if (!response.data) throw new Error('Failed to fetch grading status');
        return response.data;
    }

    // Mark or unmark session as graded
    async setSessionGraded(sessionId: string, graded: boolean): Promise<void> {
        const response = await apiClient.put<{ success: boolean; message: string }>(`/assessments/${sessionId}/graded`, { graded });
        if (!response.success) throw new Error(response.message || 'Failed to update grading status');
    }
}

export const assessmentService = new AssessmentService();
export default assessmentService;
